import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useApp } from "@/context/AppContext";
import API from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const DURATIONS = [7, 15, 30];

export default function SponsorApply() {
  const navigate = useNavigate();
  const { categories, catName } = useApp();
  const [f, setF] = useState({
    business_name: "", city: "", category: "", duration_days: 7,
    contact_name: "", contact_email: "", contact_phone: "", website: "", note: "",
  });
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.business_name.trim() || !f.category) { toast.error("Inserisci nome attività e categoria"); return; }
    setSaving(true);
    try {
      await API.post("/sponsor/apply", f);
      setSent(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  if (sent) {
    return (
      <Layout>
        <div className="max-w-md mx-auto text-center bg-white rounded-[18px] border border-[#F6DC7A] p-8 fade-in" data-testid="sponsor-success">
          <CheckCircle2 size={44} className="mx-auto text-[#2E7D32] mb-3" />
          <h1 className="font-serif text-2xl font-bold text-[#0F1230] mb-2">Richiesta inviata!</h1>
          <p className="text-[#2A2E48] mb-5">Grazie. Ti contattiamo a breve per attivare la promozione della tua attività in evidenza.</p>
          <Button data-testid="sponsor-success-home" onClick={() => navigate("/")} className="rounded-full bg-[#1F4FE0] text-white">Torna alla home</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-lg mx-auto fade-in">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-[#4A4E68] mb-3 text-sm"><ArrowLeft size={16} /> Indietro</button>
        <div className="bg-white rounded-[18px] border border-[#DADFEA] p-6">
          <h1 className="font-serif text-2xl font-bold text-[#0F1230] inline-flex items-center gap-2"><Sparkles size={22} className="text-[#E8590C]" /> Promuovi la tua attività</h1>
          <p className="text-sm text-[#4A4E68] mt-1 mb-5">Mettila in evidenza nella sezione <b>Cerca</b> per la tua zona e categoria. Compila i dati: ti contattiamo noi per attivarla. Nessun pagamento online adesso.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="sponsor-form">
            <div>
              <Label>Nome attività *</Label>
              <Input data-testid="sponsor-name" value={f.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Es. Idraulica Rossi" className="mt-1" />
            </div>
            <div>
              <Label>Città</Label>
              <Input data-testid="sponsor-city" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Es. la tua città" className="mt-1" />
            </div>
            <div>
              <Label>Categoria *</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {categories.filter((c) => c.slug !== "varie").map((c) => (
                  <button type="button" key={c.slug} data-testid={`sponsor-cat-${c.slug}`} onClick={() => set("category", c.slug)}
                    className={`px-3 h-9 rounded-full text-sm border ${f.category === c.slug ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA] text-[#2A2E48]"}`}>{catName(c)}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>Durata</Label>
              <div className="flex gap-2 mt-1">
                {DURATIONS.map((d) => (
                  <button type="button" key={d} data-testid={`sponsor-duration-${d}`} onClick={() => set("duration_days", d)}
                    className={`px-4 h-10 rounded-full text-sm border ${f.duration_days === d ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA] text-[#2A2E48]"}`}>{d} giorni</button>
                ))}
              </div>
            </div>
            <div className="h-px bg-[#F1F1F7] my-1" />
            <p className="text-sm font-medium text-[#0F1230]">Come ti contattiamo</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nome referente</Label><Input data-testid="sponsor-contact-name" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="mt-1" /></div>
              <div><Label>Telefono</Label><Input data-testid="sponsor-contact-phone" value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="mt-1" /></div>
              <div><Label>Email</Label><Input data-testid="sponsor-contact-email" type="email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="mt-1" /></div>
              <div><Label>Sito web (facoltativo)</Label><Input data-testid="sponsor-website" value={f.website} onChange={(e) => set("website", e.target.value)} className="mt-1" /></div>
            </div>
            <div><Label>Note (facoltativo)</Label><Textarea data-testid="sponsor-note" value={f.note} onChange={(e) => set("note", e.target.value)} className="mt-1" placeholder="Qualcosa che vuoi dirci?" /></div>
            <Button data-testid="sponsor-submit" type="submit" disabled={saving} className="w-full h-12 rounded-full bg-[#E8590C] hover:bg-[#B8420A] text-white">Invia richiesta</Button>
            <p className="text-xs text-[#4A4E68] text-center">Inviando accetti di essere ricontattato per attivare la promozione.</p>
          </form>
        </div>
      </div>
    </Layout>
  );
}
